import type { FC, ReactNode } from 'react'
import type { LayoutData } from '@/types/content'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Wordmark } from '@/components/media/wordmark'
import { MobileMenu } from '@/components/site/mobile-menu'
import { useSearch } from '@/components/site/search-dialog'
import { mainNav } from '@/constants/site'

interface HeaderProps {
    layout: LayoutData
    overlay?: boolean
}

const isActive = (pathname: string, href: string): boolean => href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)

export const Header: FC<HeaderProps> = ({ layout, overlay = false }): ReactNode => {
    const router = useRouter()
    const { openSearch } = useSearch()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const path = router.asPath.split('?')[0].split('#')[0]

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const solid = scrolled || !overlay

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ease-cinematic',
                    layout.preview ? 'top-10' : 'top-0',
                    solid ? 'border-b border-white/[0.06] bg-obsidian/80 backdrop-blur-xl' : 'border-b border-transparent bg-transparent'
                )}
            >
                <div className='shell flex h-16 items-center justify-between gap-6 lg:h-20'>
                    <Link href='/' aria-label='Serene Corpse, home' className='relative z-10 shrink-0 py-2'>
                        <Wordmark />
                    </Link>

                    <nav aria-label='Main' className='hidden xl:block'>
                        <ul className='flex items-center gap-1'>
                            {mainNav.map(item => {
                                const active = isActive(path, item.href)

                                return (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'relative inline-flex h-10 items-center px-3 text-[0.68rem] font-medium uppercase tracking-[0.2em] transition-colors duration-300',
                                                active ? 'text-ivory' : 'text-ivory/60 hover:text-ivory'
                                            )}
                                        >
                                            {item.label}
                                            <span
                                                className={cn(
                                                    'absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-gold transition-opacity duration-300',
                                                    active ? 'opacity-100' : 'opacity-0'
                                                )}
                                                aria-hidden='true'
                                            />
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-1.5'>
                        <button
                            type='button'
                            onClick={openSearch}
                            className='inline-flex h-10 items-center gap-2.5 rounded-full border border-ivory/10 px-3 text-ivory/70 transition hover:border-ivory/30 hover:text-ivory sm:px-4'
                            aria-label='Search the site'
                        >
                            <Search className='size-4' aria-hidden='true' />
                            <span className='hidden text-xs sm:inline'>Search</span>
                            <kbd className='hidden rounded border border-ivory/15 px-1.5 py-0.5 font-sans text-[0.6rem] text-ivory/45 lg:inline'>
                                Ctrl K
                            </kbd>
                        </button>

                        <button
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            className='inline-flex size-10 items-center justify-center rounded-full text-ivory transition hover:bg-ivory/[0.06] xl:hidden'
                            aria-label='Open menu'
                            aria-expanded={menuOpen}
                        >
                            <Menu className='size-5' aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </header>

            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} onSearch={openSearch} layout={layout} />
        </>
    )
}
